import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiToggleLeft, FiToggleRight, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { getFormSettings, setFormActive } from '../services/supabaseClient';
import { FORMS } from '../config/forms';

const AdminForms = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', message }

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { success, data, error: err } = await getFormSettings();
    if (success) {
      setSettings(data || {});
    } else {
      setError(err || 'Erro ao carregar configurações dos formulários.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 3000);
  };

  const handleToggle = async (formId, currentActive) => {
    const nextActive = !currentActive;
    // Optimistic UI
    setSettings((prev) => ({ ...prev, [formId]: nextActive }));
    setSavingId(formId);
    const { success, error: err } = await setFormActive(formId, nextActive, user?.id || null);
    setSavingId(null);
    if (success) {
      showFeedback('success', `Formulário ${nextActive ? 'habilitado' : 'desabilitado'} com sucesso.`);
    } else {
      // Reverter em caso de erro
      setSettings((prev) => ({ ...prev, [formId]: currentActive }));
      showFeedback('error', err || 'Erro ao atualizar formulário.');
    }
  };

  // Segurança extra: se não for admin, não renderiza a gestão
  if (user?.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4">
          <p className="font-medium">Acesso negado</p>
          <p className="text-sm mt-1">Apenas administradores podem gerenciar formulários.</p>
        </div>
        <Link to="/forms" className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700">
          <FiArrowLeft className="mr-2" /> Voltar para formulários
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/forms" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2">
          <FiArrowLeft className="mr-1" /> Voltar
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Gerenciar Formulários</h1>
        <p className="mt-2 text-gray-600">
          Habilite ou desabilite os formulários disponíveis para os usuários. Formulários desabilitados
          não aparecem na lista e o acesso direto pela URL é bloqueado.
        </p>
      </div>

      {feedback && (
        <div
          className={`mb-4 flex items-center rounded-md p-3 text-sm ${
            feedback.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {feedback.type === 'success' ? (
            <FiCheck className="mr-2 h-5 w-5" />
          ) : (
            <FiAlertCircle className="mr-2 h-5 w-5" />
          )}
          {feedback.message}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4">
          <p className="font-medium">Erro</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={fetchSettings}
            className="mt-3 text-sm font-medium text-red-700 underline hover:text-red-800"
          >
            Tentar novamente
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {FORMS.map((form) => {
            const isActive = settings[form.id] !== false; // default true
            const isSaving = savingId === form.id;
            return (
              <div
                key={form.id}
                className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-5"
              >
                <div className="flex items-start min-w-0">
                  <div className="flex-shrink-0">{form.icon}</div>
                  <div className="ml-4 min-w-0">
                    <h3 className="text-lg font-medium text-gray-900">{form.title}</h3>
                    <p className="mt-1 text-sm text-gray-600">{form.description}</p>
                    <p className="mt-2 text-xs text-gray-400 font-mono">{form.id}</p>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => handleToggle(form.id, isActive)}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                      isActive ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    aria-pressed={isActive}
                    aria-label={isActive ? 'Desabilitar formulário' : 'Habilitar formulário'}
                    title={isActive ? 'Clique para desabilitar' : 'Clique para habilitar'}
                  >
                    {isSaving ? (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                      </span>
                    ) : (
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          isActive ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    )}
                  </button>
                  <div className="mt-1 flex items-center justify-center text-xs">
                    {isActive ? (
                      <span className="inline-flex items-center text-green-600">
                        <FiToggleRight className="mr-1 h-3 w-3" /> Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-gray-500">
                        <FiToggleLeft className="mr-1 h-3 w-3" /> Inativo
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminForms;
